/**
 * The mid-attack lock and held actions — open/close the acting actor's attack lock, and the
 * self-enforcing `prepare`/`drop_prepare` pair.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok } from "@lancer-vtt/shared";
import { activeAttack, canUseAction, heldAction, quickTechOptionKey, recordSpend, type TurnBudget } from "../types";
import { changed, requireCombatant, type EncounterHandler } from "./context";

export const applyOpenAttack: EncounterHandler<"open_attack"> = (state, cmd) => {
  // Mid-attack lock: record that a sequential
  // attack action (a Barrage, or a lock-open Skirmish) is in progress and APPEND the fired
  // weapon to the acting actor's lock (created if absent, stamping `action`) — the room folds
  // this command for BOTH the opener AND every continuation/rider shot, not just the opener,
  // so `AttackLock.weapons` tracks every shot fired this action, however many. Like
  // `spend_action`/`grant_overcharge` the reducer ENFORCES NOTHING — the opener/continuation
  // legality is the room's pre-resolve wall; this only sets state. A no-op when nobody is
  // acting (defensive). The whole prior activation is captured into the `restore_budget`
  // inverse (lossless), so undoing any one shot's batch pops just that append back off.
  const a = state.activation;
  const turn =
    a.turn.phase === "acting"
      ? {
          ...a.turn,
          attack: {
            action: cmd.action,
            weapons: [...(a.turn.attack?.weapons ?? []), cmd.weapon],
            // Committed mounts: named by the opener; absent keeps the existing set.
            ...(cmd.mounts !== undefined
              ? { mounts: cmd.mounts }
              : a.turn.attack?.mounts !== undefined
                ? { mounts: a.turn.attack.mounts }
                : {}),
            // Ordnance turn-virgin snapshot:
            // stamped ONCE, at the true opener, and PRESERVED unchanged on every later
            // continuation/rider append (`cmd.ordnanceWindow` is `undefined` on those — the
            // room only computes/passes it for the opener) — never re-derived mid-attack.
            ...(cmd.ordnanceWindow !== undefined
              ? { ordnanceWindow: cmd.ordnanceWindow }
              : a.turn.attack?.ordnanceWindow !== undefined
                ? { ordnanceWindow: a.turn.attack.ordnanceWindow }
                : {}),
          },
        }
      : a.turn;
  return ok({
    state: { ...state, activation: { ...a, turn } },
    inverse: { type: "restore_budget", actorId: cmd.actorId, activation: a },
    events: changed(cmd.combatantId),
  });
};

export const applyCloseAttack: EncounterHandler<"close_attack"> = (state, cmd) => {
  // Mid-attack lock: clear the acting actor's attack lock — auto-folded when no
  // legal remaining attack is left (a Barrage: Superheavy / 2 weapons fired; a Skirmish: its
  // one rider fired), or alone for an explicit `end_attack` (abandon-after-one). Enforces
  // nothing; a no-op when nobody is acting / no lock open. The `restore_budget` inverse captures
  // the whole prior activation, so undo restores the lock (and its recorded weapons) exactly.
  const a = state.activation;
  let turn = a.turn;
  if (a.turn.phase === "acting") {
    const { attack: _drop, ...rest } = a.turn;
    turn = rest;
  }
  return ok({
    state: { ...state, activation: { ...a, turn } },
    inverse: { type: "restore_budget", actorId: cmd.actorId, activation: a },
    events: changed(cmd.combatantId),
  });
};

export const applyConvertBarrageToSkirmish: EncounterHandler<"convert_barrage_to_skirmish"> = (state, cmd) => {
  // Barrage → Skirmish re-price (`decisions/0049`, 2026-09-24 amendment): refund the second
  // action point, move one ledger use from `barrage` to `skirmish`, and drop the lock. The room's
  // `stop_barrage_as_skirmish` gate is the legality wall; this only sets state. A no-op when
  // nobody is acting. The `restore_budget` inverse captures the whole prior activation, so undo
  // restores the Full spend, the `barrage` ledger entry and the open lock together.
  const a = state.activation;
  let turn = a.turn;
  if (a.turn.phase === "acting") {
    const { attack: _drop, ...rest } = a.turn;
    const budget = a.turn.budget;
    const uses = { ...budget.actionUses };
    const barrageUses = (uses.barrage ?? 0) - 1;
    if (barrageUses > 0) uses.barrage = barrageUses;
    else delete uses.barrage;
    uses.skirmish = (uses.skirmish ?? 0) + 1;
    turn = {
      ...rest,
      budget: {
        ...budget,
        actions: budget.actions + 1,
        spent: recordSpend(budget, { actions: -1 }),
        actionUses: uses,
      },
    };
  }
  return ok({
    state: { ...state, activation: { ...a, turn } },
    inverse: { type: "restore_budget", actorId: cmd.actorId, activation: a },
    events: changed(cmd.combatantId),
  });
};

export const applyPrepare: EncounterHandler<"prepare"> = (state, cmd) => {
  // Prepare: a Tier-1, user-facing
  // gameplay command that self-enforces its DOMAIN-only legality here, exactly like
  // `boost` — the RULES-layer gates (Ordnance
  // window, Loading, status legality for the readied verb) are checked by the room
  // BEFORE this command is issued (`encounterRoom.ts`'s `applyPrepare`), since
  // `@lancer-vtt/domain` cannot import `@lancer-vtt/rules`.
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const a = state.activation;
  if (a.turn.phase !== "acting") {
    return err(new CommandError(`prepare: no active actor — only the unit mid-turn can Prepare`));
  }
  if (a.turn.actor !== c.id) {
    return err(new CommandError(`prepare: combatant ${c.id} is not the active actor (${a.turn.actor})`));
  }
  // At most one held action per combatant — re-Preparing while already
  // holding is rejected here (the lockout below would ALSO catch this once wired at
  // every choke point, but this is the direct, specific reason).
  if (heldAction(a, c.id)) {
    return err(new CommandError(`prepare: combatant ${c.id} is already holding a prepared action — drop_prepare first`));
  }
  // Mid-attack lock (gotcha — verified explicitly, not left to leak): Prepare is a
  // quick action, so it's blocked by the SAME "finish or end the attack first" wall
  // `boost` (its self-enforcing sibling here) and the room's mid-attack intent gate
  // enforce for the lock-holder's own further actions (RAW Core p.62/p.70). The standard
  // `move_token` is the ONE exception — a move is not an action.
  if (activeAttack(a)?.weapons.length) {
    return err(
      new CommandError(
        `prepare: combatant ${c.id} cannot Prepare mid-attack — finish or end the attack first (end_attack)`,
      ),
    );
  }
  if (a.turn.budget.actions < 1) {
    return err(new CommandError(`prepare: combatant ${c.id} has no Quick action left (actions ${a.turn.budget.actions})`));
  }
  const readied = cmd.held.readiedActionId;
  const allowance = c.base.actionAllowances?.[readied] ?? 1;
  // Quick Tech repeats per OPTION, not per action (RAW Core p.69: "a different option must be
  // chosen every time"), so a readied Invade counts — and stamps — its `quick_tech:<option>` slot
  // beside `"quick_tech"`, exactly as a live use does (`rules`' `quickTechOptionLedgerId`). A Scan
  // earlier this turn therefore doesn't block Preparing an Invade, and a released-then-dropped
  // Invade still walls a live one.
  const optionKey = readied === "quick_tech" ? quickTechOptionKey(cmd.held.weapon ?? "act_invade") : undefined;
  const countKey = optionKey ?? readied;
  if (!canUseAction(a.turn.budget, countKey, allowance)) {
    const what = optionKey !== undefined ? `this Quick Tech option (${countKey})` : readied;
    return err(new CommandError(`prepare: combatant ${c.id} has already used ${what} this turn (once per turn) — can't Prepare it`));
  }
  const uses = a.turn.budget.actionUses;
  const budget: TurnBudget = {
    ...a.turn.budget,
    actions: a.turn.budget.actions - 1,
    actionUses: {
      ...uses,
      [readied]: (uses?.[readied] ?? 0) + 1,
      ...(optionKey !== undefined ? { [optionKey]: (uses?.[optionKey] ?? 0) + 1 } : {}),
    },
    // Prepare charges its Quick action HERE rather than through `spend_action`, so the
    // cost-keyed spend counter has to be folded here too. **Not one of the four sites
    // `projects/action-ordering-audit.md` D2 enumerates** — D2's list missed this one (and
    // `stand_up`'s move charge). Left out, a Prepare would spend a point the counter never
    // saw, and the window Phase 1.3 keys on `spent` would stay open after one, where today's
    // `budget.actions === ACTIONS_PER_TURN` equality closes it.
    spent: recordSpend(a.turn.budget, { actions: 1 }),
  };
  return ok({
    state: {
      ...state,
      activation: {
        ...a,
        // No `attack` lock to preserve here — the reject above already guarantees none is
        // open by the time this mutation runs (mirrors `boost`'s own fresh `turn`).
        turn: { phase: "acting", actor: c.id, budget },
        heldActions: { ...a.heldActions, [c.id]: cmd.held },
      },
    },
    inverse: { type: "restore_budget", actorId: cmd.actorId, activation: a },
    events: changed(c.id),
  });
};

export const applyDropPrepare: EncounterHandler<"drop_prepare"> = (state, cmd) => {
  // Voluntary drop: clear the hold. A no-op — never
  // rejects — when nothing is held (mirrors `close_attack`'s defensive shape). Lifts
  // the lockout immediately: every choke point reads `heldActions` live, so clearing
  // the entry IS the unlock, nothing else to restore. The `restore_budget` inverse
  // carries the whole prior activation, so undo re-arms the exact dropped record.
  const a = state.activation;
  if (!heldAction(a, cmd.combatantId)) {
    return ok({ state, inverse: { type: "drop_prepare", actorId: cmd.actorId, combatantId: cmd.combatantId }, events: changed(null) });
  }
  const { [cmd.combatantId]: _dropped, ...rest } = a.heldActions;
  return ok({
    state: { ...state, activation: { ...a, heldActions: rest } },
    inverse: { type: "restore_budget", actorId: cmd.actorId, activation: a },
    events: changed(cmd.combatantId),
  });
};
